/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { AdminDashUserResponse } from "@packages/sdk";
import { CommunityType, useRequestAdminDashboardMember } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  constantSemanticColors,
  dropShadow,
  Edit2Icon,
  END_OF_BROWSER_ASYNC_QUEUE,
  IconButton,
  MinusIcon,
  ModalDropdownMenu,
  ModalDropdownMenuItem,
  MoreIcon,
  spacing,
  SwapIcon,
  TrashIcon,
  useConfirm,
  useModal,
} from "@packages/ui";
import { useState } from "react";

import { useAdminDashMemberActions } from "../../../../lib/hooks/useAdminDashMemberActions";
import { useRequestCommunityAdminMembersDelete } from "../../../../requests/useRequestCommunityAdminMembersDelete";
import { useRequestCommunityAdminRosterMembersDelete } from "../../../../requests/useRequestCommunityAdminRosterMembersDelete";
import { MemberModal } from "../MemberModal";
import { ResetPasswordMenuItem } from "../ResetPasswordMenuItem";

const deleteButtonStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.errorError,
});

const dropdownMenuItemStyles: Interpolation = {
  minWidth: "225px",
};

const dropdownMenuStyles: Interpolation = {
  boxShadow: `${dropShadow.none} ${dropShadow.ms} ${dropShadow.m} ${dropShadow.none} ${constantSemanticColors.fixedDarkenVeryLow}`,
  marginRight: spacing.l,
};

const dropdownMenuItemErrorStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  color: theme.semanticColors.errorMedium,
});

const dropdownMenuItemContentStyles: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
};

const modalStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  padding: spacing.xl,
};

type EditMemberDropdownMenuProps = {
  communityId: number;
  communityType: CommunityType;
  user: AdminDashUserResponse & {
    is_only_admin?: boolean;
  };
};

export const EditMemberDropdownMenu = ({
  communityId,
  communityType,
  user,
}: EditMemberDropdownMenuProps) => {
  const { track } = useAnalytics();
  const [isEnabled, setIsEnabled] = useState(false);
  const t = useTranslations();
  const confirmDelete = useConfirm({
    buttonFormat: "vertical-reverse",
    cancelButton: (handleConfirm, ...props) => (
      <Button
        isFullWidth
        onClick={handleConfirm}
        size="l"
        variant="neutral"
        {...props}
      >
        <I18nMessage message="admin_dash_cancel" />
      </Button>
    ),
    iconButtonProps: {
      variant: "transparentNeutral",
      size: "s",
    },
    modalProps: {
      contentCSS: [modalStyles],
    },
    primaryButton: (handleConfirm, ...props) => (
      <Button
        isFullWidth
        onClick={handleConfirm}
        size="l"
        css={deleteButtonStyles}
        variant="neutral"
        {...props}
      >
        <I18nMessage message="admin_dash_delete" />
      </Button>
    ),
    showCloseButton: true,
    size: "m",
  });
  const editModal = useModal(MemberModal);
  const { handleChangeAdminRights, handleRemoveFromGroup } =
    useAdminDashMemberActions({ communityId, user });

  const analytics = useAnalytics();

  const deleteUserGeneral = useRequestCommunityAdminMembersDelete({
    id: communityId,
  });
  const deleteUserRoster = useRequestCommunityAdminRosterMembersDelete({
    id: communityId,
  });
  const { mutationPut: editUser } = useRequestAdminDashboardMember({
    communityId,
    enabled: isEnabled,
    userId: user.id,
  });

  const canEditAndDelete = communityType === "school";
  const canPromoteAndDemote = communityType !== "school";
  const canRemoveFromGroup = communityType !== "school";

  return (
    <ModalDropdownMenu
      contentCSS={[dropdownMenuStyles]}
      onOpenChange={(newOpen) => setIsEnabled(newOpen)}
      trigger={
        <IconButton
          icon={<MoreIcon />}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onPointerDown={() => {
            analytics.track({
              event: HallowAnalyticsEvent.TappedCommunityMemberOptions,
            });
          }}
          variant="transparentNeutral"
        />
      }
    >
      {canPromoteAndDemote && (
        <ModalDropdownMenuItem
          modalItemProps={{
            onPointerDown: (e) => {
              e.stopPropagation();
              handleChangeAdminRights();
            },
          }}
          dropdownMenuItemProps={{
            onClick: (e) => {
              e.stopPropagation();
              handleChangeAdminRights();
            },
          }}
          dropdownMenuItemCSS={[dropdownMenuItemStyles]}
          modalItemCSS={[dropdownMenuItemStyles]}
        >
          <div css={dropdownMenuItemContentStyles}>
            <SwapIcon />
            {user.is_admin
              ? t("community_member_option_assign_as_member")
              : t("community_member_option_assign_as_admin")}
          </div>
        </ModalDropdownMenuItem>
      )}
      {canRemoveFromGroup && (
        <ModalDropdownMenuItem
          modalItemProps={{
            onPointerDown: (e) => {
              e.stopPropagation();
              handleRemoveFromGroup();
            },
          }}
          dropdownMenuItemProps={{
            onClick: (e) => {
              e.stopPropagation();
              handleRemoveFromGroup();
            },
          }}
          dropdownMenuItemCSS={[
            dropdownMenuItemStyles,
            dropdownMenuItemErrorStyles,
          ]}
          modalItemCSS={[dropdownMenuItemStyles, dropdownMenuItemErrorStyles]}
        >
          <div css={dropdownMenuItemContentStyles}>
            <MinusIcon.Outline />
            {user.is_self
              ? t("community_member_option_leave_group")
              : t("community_member_option_remove")}
          </div>
        </ModalDropdownMenuItem>
      )}
      {canEditAndDelete && (
        <>
          <ModalDropdownMenuItem
            modalItemProps={{
              onClick: (e) => e.stopPropagation(),
            }}
            dropdownMenuItemProps={{
              onClick: (e) => e.stopPropagation(),
            }}
            onSelect={() => {
              // avoid modal conflicts with a timeout
              setTimeout(
                () =>
                  editModal.show({
                    user,
                    initialState: "edit",
                    communityType,
                    closeOnDone: true,
                    onSubmit: async (values) => {
                      await editUser.mutateAsync({
                        body: values,
                        params: {
                          path: { community_id: communityId, user_id: user.id },
                        },
                      });
                    },
                  }),
                END_OF_BROWSER_ASYNC_QUEUE,
              );
            }}
          >
            <div css={dropdownMenuItemContentStyles}>
              <Edit2Icon />
              <I18nMessage message="admin_dash_edit_member" />
            </div>
          </ModalDropdownMenuItem>
          <ResetPasswordMenuItem
            user={user}
            communityId={communityId}
            screenName="members"
          />
          <ModalDropdownMenuItem
            modalItemProps={{
              onClick: (e) => e.stopPropagation(),
            }}
            dropdownMenuItemProps={{
              onClick: (e) => e.stopPropagation(),
            }}
            dropdownMenuItemCSS={[
              dropdownMenuItemStyles,
              dropdownMenuItemErrorStyles,
            ]}
            modalItemCSS={[dropdownMenuItemStyles, dropdownMenuItemErrorStyles]}
            onSelect={async () => {
              const confirmed = await confirmDelete.show({
                title: t("admin_dash_delete_confirmation_title"),
                description: t("admin_dash_delete_confirmation_subtitle"),
              });
              if (confirmed) {
                track({
                  event: HallowAnalyticsEvent.TappedDeleteMember,
                });
                const deleteUser =
                  communityType === CommunityType.school
                    ? deleteUserRoster
                    : deleteUserGeneral;
                await deleteUser.mutateAsync({
                  body: { user_ids: [user.id] },
                  params: {
                    path: { community_id: communityId },
                  },
                });
              }
            }}
          >
            <div css={dropdownMenuItemContentStyles}>
              <TrashIcon />
              <I18nMessage message="general_word_delete" />
            </div>
          </ModalDropdownMenuItem>
        </>
      )}
    </ModalDropdownMenu>
  );
};
